import type { Directive } from 'vue'

type THtmlValue = string | null | undefined

const toHtml = (value: THtmlValue) => (value == null ? '' : String(value))

/**
 * Whether the element already holds this HTML. Both sides go through the browser's
 * parser, so entities, void tags and attribute quotes compare equal.
 */
const hasHtml = (el: HTMLElement, html: string) => {
  const template = document.createElement('template')
  template.innerHTML = html
  return template.innerHTML === el.innerHTML
}

/**
 * Replacement for v-html that keeps the server-rendered DOM on hydration.
 *
 * Since Vue 3.5.39 hydration re-assigns every dynamic prop, and v-html compiles to a
 * dynamic innerHTML prop: the browser re-parses identical HTML and recreates the nodes.
 * The SEO texts are the LCP elements of most pages, so the LCP moved from the first paint
 * to the end of hydration (#296, vuejs/core#15138). The HTML is rendered on the server
 * through getSSRProps and set on the client only when it differs from the DOM.
 */
export const vHydratedHtml: Directive<HTMLElement, THtmlValue> = {
  getSSRProps: ({ value }) => ({ innerHTML: toHtml(value) }),
  mounted(el, { value }) {
    const html = toHtml(value)
    if (!hasHtml(el, html)) el.innerHTML = html
  },
  updated(el, { value, oldValue }) {
    if (value !== oldValue) el.innerHTML = toHtml(value)
  }
}

export default defineNuxtPlugin((nuxtApp) => {
  nuxtApp.vueApp.directive('hydrated-html', vHydratedHtml)
})
